///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Layer visibility helpers shared by the Layers panel and the ⌘K palette:
/// is a layer on the globe, is a sub-layer disabled by a hidden parent,
/// how many top-level layers are on, toggle, and open a layer's browser.
///

import { useCommandRegistry } from "@/hooks/useCommandRegistry"
import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import type { DatasetType } from "@/types/DatasetElement"
import { useCallback, useMemo } from "react"
import { LAYERS, type LayerDefinition } from "./layerRegistry"

export function useLayerVisibility() {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const { commands: registryCommands } = useCommandRegistry()

	const layerVisible = useCallback((type: DatasetType) => {
		return visibleDatasets.includes(type)
	}, [visibleDatasets])

	// Sub-layers are disabled while their parent layer is hidden
	const layerDisabled = useCallback((layer: LayerDefinition) => {
		if (!layer.parentId) return false
		const parent = LAYERS.find(l => l.id === layer.parentId)
		return !!parent?.visibility && !visibleDatasets.includes(parent.visibility)
	}, [visibleDatasets])

	// Sub-layers modify their parent's display and don't count separately
	const activeCount = useMemo(() => {
		return LAYERS.filter(layer => !layer.parentId && layer.visibility && layerVisible(layer.visibility)).length
	}, [layerVisible])

	const toggleLayer = useCallback((type: DatasetType, checked: boolean) => {
		dispatch(sendDatasetAction({ type, action: checked ? "enable" : "disable" }))
	}, [dispatch])

	/** Open the layer's data browser; returns false when it has none */
	const openLayerTable = useCallback((layer: LayerDefinition): boolean => {
		if (!layer.commandId) return false
		const command = registryCommands.find(cmd => cmd.id === layer.commandId)
		if (!command) return false
		command.onSelect()
		return true
	}, [registryCommands])

	return { layerVisible, layerDisabled, activeCount, toggleLayer, openLayerTable }
}
